import React, { useContext, useEffect, useState, KeyboardEvent } from "react";
import { AuthContext } from "../../contexts/Auth";
import { FiltersContext } from "../../contexts/Filters";
import {
  RefundRequest,
  RefundRequestFilters,
  RefundRequestStatusFilter,
  RefundRequestStatusCountMap,
  RefundRequestsResponse,
  refundRequestStatusFilterValues,
} from "../../../common/types";
import {
  fetchBudgetSummary,
  fetchRefundRequests,
  updateBudget,
  fetchRefundRequestStatusCountMap,
  fetchRefundRequestsUsingSubOrderId,
} from "../../actions/refunds";
import { RefundRequestsPagination } from "../../components/RefundRequestsPagination";
import { RefundRequestTable } from "../../components/RefundRequestTable";
import { Screen } from "../../components/Screen";
import { Breadcrumbs, Panel, TabsControlled, IconInput } from "@glamcorner/components";
import "./refund-requests-screen.scss";
import { AppRouterContext } from "../../AppRouter";
import { Heading } from "../../components/Heading";
import { BudgetSummary, BudgetSummaryNumbers } from "../../components/BudgetSummary";
import { formatRefundRequestStatus } from "../../utils";
import SearchIcon from "@glamcorner/components/lib/assets/icons/search-icn.inline.svg";
import { encodeSubOrderId } from "../../../common/utils";
import { stringToRefundRequestStatusFilter } from "../../mappers";

export const RefundRequestsScreen: React.FC = () => {
  const { routerProps } = useContext(AppRouterContext);
  const { fetchToken } = useContext(AuthContext);
  const { status, setStatus, cursor, setCursor, goBackwards, setGoBackwards, subOrderId, setSubOrderId } = useContext(
    FiltersContext
  );
  const [tokenForLink, setTokenForLink] = useState<string | undefined>(undefined);
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const hasResults = refundRequests.length > 0;

  const [budgetSummary, setBudgetSummary] = useState<BudgetSummaryNumbers>({});
  const [refundRequestStatusCountMap, setRefundRequestStatusCountMap] = useState<RefundRequestStatusCountMap>({});
  const [inputSubOrderId, setInputSubOrderId] = useState<string>(subOrderId);

  const fetchData = async (
    passedCursor: string,
    passedStatus: RefundRequestStatusFilter,
    passedGoBackwards: boolean,
    passedSubOrderId: string
  ) => {
    const token = await fetchToken();
    setTokenForLink(token);
    let res: RefundRequestsResponse;

    if (passedSubOrderId.length) {
      res = await fetchRefundRequestsUsingSubOrderId(token, passedSubOrderId);
    } else {
      res = await fetchRefundRequests(token, passedCursor, passedStatus, passedGoBackwards);
    }

    const refunds = res.body;
    setRefundRequests(refunds);
    setStatus(passedStatus);
    setCursor(passedCursor);
    setGoBackwards(passedGoBackwards);
    setSubOrderId(passedSubOrderId);

    const hasMore = res.metadata.hasMoreData;
    if (passedGoBackwards) {
      setHasPrev(hasMore);
      setHasNext(true);
    } else {
      setHasNext(hasMore);
      setHasPrev(!!passedCursor);
    }
  };

  const fetchBudgetSummaryData = async () => {
    const token = await fetchToken();
    const budgetSummaryNumbers = await fetchBudgetSummary({ token });
    setBudgetSummary(budgetSummaryNumbers);
  };

  const fetchRefundRequestStatusCountMapData = async () => {
    const token = await fetchToken();
    setRefundRequestStatusCountMap(await fetchRefundRequestStatusCountMap(token));
  };

  const updateBudgetOnServer = async (newBudgetInCents: number) => {
    const token = await fetchToken();
    await updateBudget({ token, budgetInCents: newBudgetInCents });
  };

  useEffect(() => {
    fetchData(cursor, status, goBackwards, subOrderId);
    fetchBudgetSummaryData();
    fetchRefundRequestStatusCountMapData();
  }, []);

  const refundStatusOnChangeHandler = (value: string) => {
    setInputSubOrderId("");
    fetchData("", stringToRefundRequestStatusFilter(value), false, "");
  };

  const onTableRowClick = (id: string) => {
    routerProps.history.push(`/${id}`);
  };

  const onClickChangeBudget = (event: any) => {
    event.preventDefault();
    const newBudgetDollarsString = prompt("New cash budget");
    if (newBudgetDollarsString) {
      const newBudgetInCents: number = (newBudgetDollarsString.replace(/[^0-9\.]/g, "") as any) * 100;
      setBudgetSummary({ ...budgetSummary, budgetInCents: newBudgetInCents });
      updateBudgetOnServer(newBudgetInCents);
    }
  };

  const tabsData: { value: string; name: string }[] = refundRequestStatusFilterValues.map(filterStatus => ({
    name: `${formatRefundRequestStatus(filterStatus)} (${refundRequestStatusCountMap[filterStatus] || 0})`,
    value: filterStatus,
  }));

  const inputSubOrderIdOnChangeHandler = (newInputSubOrderId: string) => {
    setInputSubOrderId(newInputSubOrderId);
    if (!newInputSubOrderId.length) {
      fetchData(cursor, status, goBackwards, "");
    }
  };
  const inputSubOrderIdOnKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      fetchData("", RefundRequestFilters.All, false, encodeSubOrderId(inputSubOrderId));
    }
  };

  return (
    <Screen className="c-refund-requests-screen">
      <Heading />
      <div className="c-refund-requests-screen__top-right-container">
        <BudgetSummary budgetSummary={budgetSummary} onClickChangeBudget={onClickChangeBudget} />
      </div>
      <div className="c-refund-requests-screen__breadcrumbs-container">
        <Breadcrumbs className="l-m-hidden" breadcrumbs={[{ label: "Refund Requests", href: null }]} />
      </div>
      <Panel>
        <div className="c-refund-requests-screen__header-container">
          <div className="c-refund-requests-screen__header-tabs">
            <TabsControlled selected={status} data={tabsData} onChange={value => refundStatusOnChangeHandler(value)} />
          </div>
          <div className="c-refund-requests-screen__sub-order-id-search-container">
            <IconInput
              value={inputSubOrderId}
              placeholder="Search Sub Order ID"
              onChange={inputSubOrderIdOnChangeHandler}
              onKeyDown={inputSubOrderIdOnKeyDownHandler}
              icon={<SearchIcon />}
            />
          </div>
        </div>

        {hasResults ? (
          <RefundRequestTable refundRequests={refundRequests} onTableRowClick={onTableRowClick} />
        ) : (
          <div className="c-refund-requests-screen__no-results">
            <div>No results found</div>
          </div>
        )}

        <RefundRequestsPagination
          hasNext={hasNext}
          hasPrev={hasPrev}
          onNext={() => {
            const lastCursor = refundRequests[refundRequests.length - 1].id;
            fetchData(lastCursor, status, false, subOrderId);
          }}
          onPrev={() => {
            const firstCursor = refundRequests[0].id;
            fetchData(firstCursor, status, true, subOrderId);
          }}
        />
      </Panel>
      <div className="c-refund-requests-screen__links">
        <a
          className="c-refund-requests-screen__link"
          href={`/api/report/upcoming?auth=${tokenForLink}`}
          target="_blank"
        >
          Upcoming report
        </a>
        <a
          className="c-refund-requests-screen__link"
          href={`/api/report/processed?auth=${tokenForLink}`}
          target="_blank"
        >
          Processed report
        </a>
      </div>
    </Screen>
  );
};
